import { useQuery } from '@tanstack/react-query'
import req from '@sportninja/common/api/request'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  findCompetitionRoster,
  toRosterChoices,
  type RosterChoiceOption,
} from '@sportninja/common/features/competition/operations/utils/rosterChoices'

interface Args {
  teamId: string | null
  /** Root competition — what an existing roster is matched against. */
  rootScheduleId: string
  enabled?: boolean
}

export interface TeamRosterChoices {
  /** Rosters this team already has, offered as clone sources. */
  choices: RosterChoiceOption[]
  /** The roster already tied to this competition, if any. */
  existing: RosterChoiceOption | null
  isLoading: boolean
}

/**
 * The clone options for the add-team roster step.
 *
 * `GET /teams/:id/rosters` returns a BARE ARRAY rather than the usual
 * `{ data }` envelope, so the response is passed to the mappers untouched —
 * destructuring `data` here would silently yield an empty list.
 */
export const useTeamRosterChoices = ({
  teamId,
  rootScheduleId,
  enabled = true,
}: Args): TeamRosterChoices => {
  'use memo'

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.teamRosterChoices.byTeam(teamId),
    queryFn: ({ signal }) => req(`/teams/${teamId}/rosters`, { signal }),
    enabled: enabled && !!teamId,
    staleTime: 30_000,
  })

  return {
    choices: toRosterChoices(data),
    existing: findCompetitionRoster(data, rootScheduleId),
    isLoading,
  }
}
